<template>
  <FormModal
    :visible="visible"
    :title="isEditing ? 'Editar procurador' : 'Adicionar procurador'"
    :icon="require('@/assets/addRepresentante.png')"
    :loading="loading"
    :confirm-label="isEditing ? 'Atualizar' : 'Adicionar'"
    @update:visible="handleClose"
    @confirm="handleSubmit"
    @cancel="handleClose"
  >
    <form @submit.prevent="handleSubmit" class="space-y-6">
      <!-- SUBSECTION: Dados Pessoais -->
      <div class="form-subsection">
        <h4 class="form-subsection-title">
          <i class="fa fa-user"></i>
          Dados Pessoais
        </h4>

        <div class="form-grid-1">
          <div class="form-field">
            <InputNome
              v-model="localData.Nome"
              label="Nome"
              :required="true"
              :erro="camposObrigatorios.erroNome"
            />
          </div>
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="tipoDocumento"
              >Tipo de Documento</label
            >
            <ModalSelect
              class="field"
              id="tipoDocumento"
              v-model="localData.TipoDocumento"
              @change="aoMudarTipoDocumento"
              :options="[{ id: '', descricao: 'Selecione' }, { id: 'CPF', descricao: 'CPF' }, { id: 'CNH', descricao: 'CNH' }, { id: 'RNE', descricao: 'RNE' }]"
              title="Tipo de Documento" placeholder="Selecione" required
            />
            <p
              v-if="camposObrigatorios.erroTipoDocumento"
              class="form-field-error"
            >
              Campo obrigatório
            </p>
          </div>
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="documento"
              >{{ obterLabelDocumento }}</label
            >
            <input
              class="field"
              type="text"
              id="documento"
              v-model="localData.CpfCnpj"
              v-mask="obterMascaraDocumento"
              :placeholder="obterPlaceholderDocumento"
              @change="validarDocumento"
            />
            <p v-if="camposObrigatorios.erroCpfCnpj" class="form-field-error">
              Campo obrigatório
            </p>
            <p v-if="erroDocumento" class="form-field-error">
              {{ mensagemErro }}
            </p>
          </div>
        </div>

        <div class="form-grid-2">
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="email"
              >E-mail</label
            >
            <input
              class="field"
              type="text"
              id="email"
              v-model="localData.Email"
              @blur="validarEmail"
            />
            <p v-if="emailErros.email" class="form-field-error">
              {{ emailErros.email }}
            </p>
          </div>
          <div class="form-field">
            <telefone-internacional
              v-model="localData.Telefone"
              label="Telefone"
              name="Telefone"
              @validate="onValidateTelefone"
            />
            <p v-if="camposObrigatorios.erroTelefone" class="form-field-error">
              Campo obrigatório
            </p>
          </div>
        </div>

        <div class="form-grid-1">
          <div class="form-field">
            <label class="form-field-label" for="cargo">Cargo</label>
            <input
              class="field"
              type="text"
              id="cargo"
              v-model="localData.Cargo"
            />
          </div>
        </div>
      </div>

      <div class="form-subsection-divider"></div>

      <!-- SUBSECTION: Endereço -->
      <div class="form-subsection">
        <h4 class="form-subsection-title">
          <i class="fa fa-map-marker"></i>
          Endereço
        </h4>

        <EnderecoFields
          ref="endereco"
          v-model="localData"
          :countries="listaPaises"
          id-prefix="procurador-address"
        />
      </div>

      <div class="form-subsection-divider"></div>

      <!-- SUBSECTION: Informações Regulatórias -->
      <div class="form-subsection">
        <h4 class="form-subsection-title">
          <i class="fa fa-shield"></i>
          Informações Regulatórias
        </h4>

        <div class="form-grid-2">
          <div class="form-field">
            <label class="form-field-label form-field-label-required"
              >É considerado US Person?</label
            >
            <div class="form-radio-group">
              <label class="form-radio-item">
                <input
                  type="radio"
                  value="false"
                  name="usPerson"
                  class="form-radio-input"
                  v-model="localData.UsPerson"
                />
                <span class="form-radio-label">Não</span>
              </label>
              <label class="form-radio-item">
                <input
                  type="radio"
                  value="true"
                  name="usPerson"
                  class="form-radio-input"
                  v-model="localData.UsPerson"
                />
                <span class="form-radio-label">Sim</span>
              </label>
            </div>
          </div>
          <div class="form-field">
            <label class="form-field-label form-field-label-required"
              >É uma Pessoa Exposta Politicamente?</label
            >
            <div class="form-radio-group">
              <label class="form-radio-item">
                <input
                  type="radio"
                  value="false"
                  name="politicamenteExposta"
                  class="form-radio-input"
                  v-model="localData.PoliticamenteExposta"
                />
                <span class="form-radio-label">Não</span>
              </label>
              <label class="form-radio-item">
                <input
                  type="radio"
                  value="true"
                  name="politicamenteExposta"
                  class="form-radio-input"
                  v-model="localData.PoliticamenteExposta"
                />
                <span class="form-radio-label">Sim</span>
              </label>
            </div>
          </div>
        </div>

        <div
          class="form-grid-1"
          v-if="user?.TipoCliente != tipoClienteEnum.PESSOA_FISICA"
        >
          <div class="form-field">
            <label
              class="form-field-label form-field-label-required"
              for="assinatura"
              >Assinatura</label
            >
            <ModalSelect
              id="assinatura"
              class="field"
              v-model="localData.Assinatura"
              :disabled="localData.TipoDocumento == 'CNPJ'"
              :options="localData.TipoDocumento == 'CNPJ' ? [{ id: '1', descricao: 'Não assina pela empresa' }] : [{ id: '', descricao: 'Selecione' }, { id: '1', descricao: 'Não assina pela empresa' }, { id: '2', descricao: 'Assina isoladamente' }, { id: '0', descricao: 'Assina em conjunto' }]"
              title="Assinatura" placeholder="Selecione" required
            />
            <p
              v-if="camposObrigatorios.erroAssinatura"
              class="form-field-error"
            >
              Campo obrigatório
            </p>
          </div>
        </div>
      </div>
    </form>
  </FormModal>
</template>

<script>
import ModalSelect from "@/components/ModalSelect.vue";
import { mapGetters } from "vuex";
import FormModal from "./FormModal.vue";
import EnderecoFields from "./EnderecoFields.vue";
import emailValidatorMixin from "@/utils/emailValidatorMixin.js";
import { mask } from "vue-the-mask";
import documentoUtils from "@/utils/documentoUtils";
import { TIPO_CLIENTE } from "@/constants/TipoClienteEnum";

export default {
  name: "ModalProcurador",
  components: {
    ModalSelect,
    FormModal,
    EnderecoFields,
  },
  directives: {
    mask,
  },
  mixins: [emailValidatorMixin],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    procurador: {
      type: Object,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      listaPaises: "GET_LISTA_PAISES",
      user: "auth/user",
    }),
    isEditing() {
      return this.localData.Guid && this.localData.Guid !== "";
    },
    // Expor TIPO_CLIENTE para o template
    tipoClienteEnum() {
      return TIPO_CLIENTE;
    },
    obterLabelDocumento() {
      if (!this.localData || !this.localData.TipoDocumento) return "CPF";
      return documentoUtils.obterLabelDocumento(this.localData.TipoDocumento);
    },
    obterMascaraDocumento() {
      if (!this.localData || !this.localData.TipoDocumento)
        return "###.###.###-##";
      return documentoUtils.obterMascaraDocumento(this.localData.TipoDocumento);
    },
    obterPlaceholderDocumento() {
      if (!this.localData || !this.localData.TipoDocumento)
        return "000.000.000-00";
      return documentoUtils.obterPlaceholderDocumento(
        this.localData.TipoDocumento,
      );
    },
  },
  data() {
    return {
      telefoneValido: {},
      emailErros: {},
      erroDocumento: false,
      mensagemErro: "",
      camposObrigatorios: {
        erroNome: false,
        erroTipoDocumento: false,
        erroCpfCnpj: false,
        erroEmail: false,
        erroTelefone: false,
        erroCep: false,
        erroLogradouro: false,
        erroNumero: false,
        erroBairro: false,
        erroCidade: false,
        erroUF: false,
        erroPais: false,
        erroAssinatura: false,
      },
      localData: {
        Guid: "",
        Nome: "",
        CpfCnpj: "",
        TipoDocumento: "",
        Email: "",
        Cargo: "",
        Telefone: "",
        Cep: "",
        Logradouro: "",
        Numero: "",
        Complemento: "",
        Bairro: "",
        Cidade: "",
        Uf: "",
        Pais: "",
        Tipo: "Procurador",
        EmissorOrdem: false,
        UsPerson: false,
        PoliticamenteExposta: false,
        Assinatura: "",
      },
    };
  },
  watch: {
    procurador: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const parsed = JSON.parse(JSON.stringify(newVal));
          Object.assign(this.localData, parsed);
          if (this.localData.TipoDocumento === "CNPJ") {
            this.localData.TipoDocumento = "";
            this.localData.CpfCnpj = "";
          }
          this.$nextTick(() => this.$refs.endereco?.refreshLookup());
        } else {
          this.resetForm();
        }
      },
    },
    visible(newVal) {
      if (!newVal) {
        this.resetValidation();
        if (!this.procurador) {
          this.resetForm();
        }
      }
    },
  },
  methods: {
    onValidateTelefone({ name, valid }) {
      if (!this.telefoneValido) {
        this.telefoneValido = {};
      }
      if (valid !== "undefined") {
        this.telefoneValido[name] = valid;
      }
    },
    validarEmail() {
      this.validarEmailGeneric(this.localData.Email, "email", {
        obrigatorio: true,
      });
    },
    aoMudarTipoDocumento() {
      if (!this.localData) return;
      this.localData.CpfCnpj = "";
      this.erroDocumento = false;
      this.mensagemErro = "";
      if (this.localData.TipoDocumento === "CNPJ") {
        this.localData.Assinatura = "1";
      }
    },
    validarDocumento() {
      if (!this.localData) return;

      if (this.localData.CpfCnpj) {
        const resultado = documentoUtils.validarDocumentoCompleto(
          this.localData.CpfCnpj,
          this.localData.TipoDocumento || "CPF",
        );
        this.erroDocumento = !resultado.valido;
        this.mensagemErro = resultado.mensagem;
      } else {
        this.erroDocumento = false;
        this.mensagemErro = "";
      }
    },
    validarFormulario() {
      let permitirAvancar = true;
      let nomeDoCampo = "";

      this.camposObrigatorios = {
        erroNome: false,
        erroTipoDocumento: false,
        erroCpfCnpj: false,
        erroEmail: false,
        erroTelefone: false,
        erroCep: false,
        erroLogradouro: false,
        erroNumero: false,
        erroBairro: false,
        erroCidade: false,
        erroUF: false,
        erroPais: false,
        erroAssinatura: false,
      };

      if (!this.localData.Nome) {
        this.camposObrigatorios.erroNome = true;
        nomeDoCampo = "Nome";
        permitirAvancar = false;
      }

      if (!this.localData.TipoDocumento) {
        this.camposObrigatorios.erroTipoDocumento = true;
        nomeDoCampo = nomeDoCampo || "Tipo de Documento";
        permitirAvancar = false;
      }

      if (!this.localData.CpfCnpj) {
        this.camposObrigatorios.erroCpfCnpj = true;
        nomeDoCampo = nomeDoCampo || this.obterLabelDocumento;
        permitirAvancar = false;
      } else if (this.erroDocumento) {
        this.camposObrigatorios.erroCpfCnpj = true;
        nomeDoCampo = nomeDoCampo || this.obterLabelDocumento;
        permitirAvancar = false;
      }

      if (Object.values(this.emailErros).some((erro) => erro !== "")) {
        this.camposObrigatorios.erroEmail = true;
        nomeDoCampo = nomeDoCampo || "E-mail";
        permitirAvancar = false;
      }

      if (!this.localData.Telefone || !this.telefoneValido?.Telefone) {
        this.camposObrigatorios.erroTelefone = true;
        nomeDoCampo = nomeDoCampo || "Telefone";
        permitirAvancar = false;
      }

      const addressValidation = this.$refs.endereco?.validate(true);
      if (!addressValidation?.valid) {
        nomeDoCampo = nomeDoCampo || addressValidation?.firstInvalidLabel || "País";
        permitirAvancar = false;
      }

      if (
        this.user?.TipoCliente != this.tipoClienteEnum.PESSOA_FISICA &&
        this.localData.TipoDocumento !== "CNPJ" &&
        (this.localData.Assinatura === null ||
          this.localData.Assinatura === undefined ||
          this.localData.Assinatura === "")
      ) {
        this.camposObrigatorios.erroAssinatura = true;
        nomeDoCampo = nomeDoCampo || "Assinatura";
        permitirAvancar = false;
      }

      if (!permitirAvancar && nomeDoCampo) {
        this.$swal({
          icon: "warning",
          title: "Oops...",
          text: `O campo ${nomeDoCampo} não foi informado, preencha a informação para avançar.`,
        });
      }

      return permitirAvancar;
    },
    handleSubmit() {
      if (this.localData.TipoDocumento === "CNPJ") {
        this.$alertWarning(
          "Documento inválido",
          "O tipo de documento CNPJ não é permitido para o procurador. Selecione CPF, CNH ou RNE antes de salvar."
        );
        return;
      }
      if (this.validarFormulario()) {
        this.$emit("submit", this.localData);
      }
    },
    handleClose() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    resetForm() {
      this.localData = {
        Guid: "",
        Nome: "",
        CpfCnpj: "",
        TipoDocumento: "",
        Email: "",
        Cargo: "",
        Telefone: "",
        Cep: "",
        Logradouro: "",
        Numero: "",
        Complemento: "",
        Bairro: "",
        Cidade: "",
        Uf: "",
        Pais: "",
        Tipo: "Procurador",
        EmissorOrdem: false,
        UsPerson: false,
        PoliticamenteExposta: false,
        Assinatura: "",
      };
      this.resetValidation();
    },
    resetValidation() {
      this.camposObrigatorios = {
        erroNome: false,
        erroTipoDocumento: false,
        erroCpfCnpj: false,
        erroEmail: false,
        erroTelefone: false,
        erroCep: false,
        erroLogradouro: false,
        erroNumero: false,
        erroBairro: false,
        erroCidade: false,
        erroUF: false,
        erroPais: false,
        erroAssinatura: false,
      };
      this.emailErros = {};
      this.erroDocumento = false;
      this.mensagemErro = "";
      this.$refs.endereco?.resetValidation();
    },
  },
};
</script>
